import React, { useState, useEffect } from 'react';
import { Building, Save, RefreshCw, Globe, Phone, Mail, MapPin, Facebook, Instagram, Twitter, MessageCircle, ExternalLink, AlertCircle, CheckCircle, Star, Music, Settings, Edit3, Trash2, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BusinessInfo = () => {
  const [activeTab, setActiveTab] = useState('business');
  const [businessInfo, setBusinessInfo] = useState({
    hospital_name: '',
    tagline: '',
    phone: '',
    email: '',
    address: '',
    timezone: 'America/New_York',
    website_title: 'Pets & Vets',
    website_subtitle: 'Animal Hospital & Urgent Care',
    business_type: 'veterinary_hospital',
    referral_hospital_name: '',
    referral_hospital_phone: '',
    facebook_link: '',
    instagram_link: '',
    twitter_link: '',
    tiktok_link: '',
    whatsapp_group_link: '',
    google_reviews_link: '',
    yelp_reviews_link: '',
    facebook_reviews_link: '',
    hero_images: [],
    // SEO Fields
    favicon_url: '',
    street_address: '',
    city: '',
    zip_code: '',
    latitude: '',
    longitude: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'saving', 'success', 'error'
  const [authError, setAuthError] = useState('');

  // Multiple Locations state
  const [multipleLocationsEnabled, setMultipleLocationsEnabled] = useState(false);
  const [additionalLocations, setAdditionalLocations] = useState([]);
  const [editingLocationIndex, setEditingLocationIndex] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Business Placeholders state
  const [businessPlaceholders, setBusinessPlaceholders] = useState([]);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);
  const [editingPlaceholder, setEditingPlaceholder] = useState(null);
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
  const [placeholderForm, setPlaceholderForm] = useState({
    name: '',
    placeholder: '',
    value: '',
    description: ''
  });

  // Business placeholder confirmation modal state
  const [showBusinessConfirmModal, setShowBusinessConfirmModal] = useState(false);
  const [businessConfirmAction, setBusinessConfirmAction] = useState(null);
  const [businessConfirmMessage, setBusinessConfirmMessage] = useState('');

  const { user, token, isAdmin, canAccessManager, loading: authLoading } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  const tabs = [
    { id: 'business', label: 'Business Info', icon: Building, color: 'var(--theme-primary)' },
    { id: 'locations', label: 'Locations', icon: MapPin, color: 'var(--theme-primary)' },
    { id: 'social', label: 'Social Media', icon: Globe, color: 'var(--theme-primary)' },
    { id: 'reviews', label: 'Review Platforms', icon: Star, color: 'var(--theme-primary)' },
    { id: 'seo', label: 'SEO Settings', icon: Settings, color: 'var(--theme-primary)' },
    { id: 'placeholders', label: 'Placeholders', icon: Settings, color: 'var(--theme-primary)' }
  ];

  // Location form state
  const [locationForm, setLocationForm] = useState({
    name: '',
    address: '',
    phone: '',
    email: ''
  });

  const clearMessage = () => {
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const fetchBusinessInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/business-info`);
      if (response.ok) {
        const data = await response.json();
        setBusinessInfo(data);
        
        // Fetch multiple locations settings
        setMultipleLocationsEnabled(data.multiple_locations_enabled || false);
        setAdditionalLocations(data.additional_locations || []);
      } else {
        throw new Error('Failed to fetch business info');
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load business information' });
      clearMessage();
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessPlaceholders = async () => {
    setLoadingPlaceholders(true);
    try {
      console.log('Fetching business placeholders from:', `${API_BASE_URL}/api/business-placeholders`);
      
      const response = await fetch(`${API_BASE_URL}/api/business-placeholders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Fetch response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Business placeholders data:', data);
        setBusinessPlaceholders(data);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch business placeholders: ${response.status} - ${errorText}`);
      }
    } catch (err) {
      console.error('Error fetching business placeholders:', err);
      setMessage({ type: 'error', text: `Failed to load business placeholders: ${err.message}` });
      clearMessage();
    } finally {
      setLoadingPlaceholders(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (token && !user) return;

    if (user && canAccessManager()) {
      setAuthError('');
      fetchBusinessInfo();
      fetchBusinessPlaceholders();
    } else if (user && !canAccessManager()) {
      setAuthError('Access denied. Manager or admin privileges required.');
      setLoading(false);
    } else if (!token) {
      setAuthError('Please log in as a manager or admin to access business information.');
      setLoading(false);
    }
  }, [user, token, authLoading]);

  const handleGoogleSync = async () => {
    setSyncing(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/business-info/sync-google`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: businessInfo.hospital_name,
          phone: businessInfo.phone,
          address: businessInfo.address
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Business information synced to Google Business Profile successfully!' });
        clearMessage();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to sync with Google Business Profile');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      clearMessage();
    } finally {
      setSyncing(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBusinessInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGeocodeAddress = async () => {
    setGeocoding(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/business-info/geocode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ use_place_id: true })
      });

      const data = await response.json();

      if (data.success) {
        setBusinessInfo(prev => ({
          ...prev,
          street_address: data.street_address || '',
          city: data.city || '',
          zip_code: data.zip_code || '',
          latitude: data.latitude || '',
          longitude: data.longitude || ''
        }));
        setMessage({ type: 'success', text: 'Address geocoded successfully from Google Place ID!' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to geocode address' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setGeocoding(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setShowModal(true);
    setModalType('saving');
    setMessage({ type: '', text: '' });

    try {
      const dataToSave = {
        ...businessInfo,
        multiple_locations_enabled: multipleLocationsEnabled,
        additional_locations: additionalLocations
      };
      
      const response = await fetch(`${API_BASE_URL}/api/business-info`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSave)
      });

      if (response.ok) {
        setModalType('success');
        setTimeout(() => {
          setShowModal(false);
          setModalType('');
        }, 2000);
      } else {
        const errorData = await response.json();
        setModalType('error');
        setMessage({ type: 'error', text: errorData.detail || 'Failed to update business information' });
        setTimeout(() => {
          setShowModal(false);
          setModalType('');
        }, 3000);
      }
    } catch (err) {
      setModalType('error');
      setMessage({ type: 'error', text: err.message });
      setTimeout(() => {
        setShowModal(false);
        setModalType('');
      }, 3000);
    } finally {
      setSaving(false);
    }
  };

  // Business placeholder management functions
  const handleCreateBusinessPlaceholder = async () => {
    if (!placeholderForm.name.trim() || !placeholderForm.placeholder.trim() || !placeholderForm.value.trim()) {
      setMessage({ type: 'error', text: 'Name, placeholder, and value are required' });
      clearMessage();
      return;
    }

    console.log('Creating business placeholder...', placeholderForm);

    // Auto-format placeholder if it doesn't have brackets
    let formattedPlaceholder = placeholderForm.placeholder;
    if (!formattedPlaceholder.startsWith('[') || !formattedPlaceholder.endsWith(']')) {
      formattedPlaceholder = `[${formattedPlaceholder.toUpperCase()}]`;
    }

    try {
      console.log('Making API request to:', `${API_BASE_URL}/api/business-placeholders`);
      
      const response = await fetch(`${API_BASE_URL}/api/business-placeholders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...placeholderForm,
          placeholder: formattedPlaceholder,
          created_by: user?.email || 'unknown'
        })
      });

      console.log('Response status:', response.status);
      const responseText = await response.text();
      console.log('Response text:', responseText);

      if (response.ok) {
        setMessage({ type: 'success', text: 'Business placeholder created successfully!' });
        clearMessage();
        setPlaceholderForm({ name: '', placeholder: '', value: '', description: '' });
        setShowPlaceholderModal(false);
        fetchBusinessPlaceholders();
      } else {
        let errorMessage = 'Failed to create placeholder';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status} - ${responseText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error creating placeholder:', err);
      setMessage({ type: 'error', text: `Error: ${err.message}` });
      clearMessage();
    }
  };

  const handleUpdateBusinessPlaceholder = async () => {
    if (!placeholderForm.name.trim() || !placeholderForm.placeholder.trim() || !placeholderForm.value.trim()) {
      setMessage({ type: 'error', text: 'Name, placeholder, and value are required' });
      clearMessage();
      return;
    }

    // Auto-format placeholder if it doesn't have brackets
    let formattedPlaceholder = placeholderForm.placeholder;
    if (!formattedPlaceholder.startsWith('[') || !formattedPlaceholder.endsWith(']')) {
      formattedPlaceholder = `[${formattedPlaceholder.toUpperCase()}]`;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/business-placeholders/${editingPlaceholder.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...placeholderForm,
          placeholder: formattedPlaceholder
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Business placeholder updated successfully!' });
        clearMessage();
        setPlaceholderForm({ name: '', placeholder: '', value: '', description: '' });
        setEditingPlaceholder(null);
        setShowPlaceholderModal(false);
        fetchBusinessPlaceholders();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update placeholder');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      clearMessage();
    }
  };

  const handleDeleteBusinessPlaceholder = async (placeholderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/business-placeholders/${placeholderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Business placeholder deleted successfully!' });
        clearMessage();
        fetchBusinessPlaceholders();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete placeholder');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      clearMessage();
    }
  };

  const confirmDeleteBusinessPlaceholder = (placeholderId, placeholderName) => {
    setBusinessConfirmMessage(`Are you sure you want to delete the business placeholder "${placeholderName}"? This action cannot be undone.`);
    setBusinessConfirmAction(() => () => handleDeleteBusinessPlaceholder(placeholderId));
    setShowBusinessConfirmModal(true);
  };

  const handleEditBusinessPlaceholder = (placeholder) => {
    setMessage({ type: '', text: '' });
    setEditingPlaceholder(placeholder);
    setPlaceholderForm({
      name: placeholder.name,
      placeholder: placeholder.placeholder,
      value: placeholder.value,
      description: placeholder.description || ''
    });
    setShowPlaceholderModal(true);
  };

  const handleCancelPlaceholderForm = () => {
    setMessage({ type: '', text: '' });
    setPlaceholderForm({ name: '', placeholder: '', value: '', description: '' });
    setEditingPlaceholder(null);
    setShowPlaceholderModal(false);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--theme-primary)' }}></div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">{authError}</p>
            <p className="text-red-600 text-sm mt-2">Please log in with a manager or admin account.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Inside the main card */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Business Information
              </h2>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Modal Popup */}
            {showModal && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 backdrop-blur-sm">
                <div className="bg-white rounded-xl px-6 py-5 max-w-xs w-full mx-4 text-center shadow-2xl border border-gray-100">
                  {modalType === 'saving' && (
                    <div className="flex items-center justify-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
                      <span className="text-sm font-medium text-gray-700">Saving...</span>
                    </div>
                  )}
                  
                  {modalType === 'success' && (
                    <div className="flex items-center justify-center gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-sm font-medium text-gray-700">Saved!</span>
                    </div>
                  )}
                  
                  {modalType === 'error' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-center gap-2">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        <span className="text-sm font-medium text-gray-700">Error</span>
                      </div>
                      <p className="text-xs text-gray-500">{message.text}</p>
                      <button
                        onClick={() => {
                          setShowModal(false);
                          setModalType('');
                        }}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-medium"
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tab Navigation - Styled like Manage Assets */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <form onSubmit={handleSubmit} className="p-3 sm:p-6">
                
                {/* Business Info Tab */}
                {activeTab === 'business' && (
                  <div className="space-y-6">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Building className="h-4 w-4 inline mr-2" />
                          Hospital Name *
                        </label>
                        <input
                          type="text"
                          name="hospital_name"
                          value={businessInfo.hospital_name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tagline
                        </label>
                        <input
                          type="text"
                          name="tagline"
                          value={businessInfo.tagline}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Your pet's health is our priority"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Globe className="h-4 w-4 inline mr-2" />
                          Website Title (Header)
                        </label>
                        <input
                          type="text"
                          name="website_title"
                          value={businessInfo.website_title}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Pets & Vets"
                        />
                        <p className="text-xs text-gray-500 mt-1">Displayed in header and footer</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Settings className="h-4 w-4 inline mr-2" />
                          Website Subtitle (Header)
                        </label>
                        <input
                          type="text"
                          name="website_subtitle"
                          value={businessInfo.website_subtitle}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Animal Hospital & Urgent Care"
                        />
                        <p className="text-xs text-gray-500 mt-1">Displayed in header and footer</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Building className="h-4 w-4 inline mr-2" />
                          Business Type
                        </label>
                        <select
                          name="business_type"
                          value={businessInfo.business_type || 'veterinary_hospital'}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="veterinary_hospital">Veterinary Hospital</option>
                          <option value="restaurant">Restaurant</option>
                          <option value="grocery_store">Grocery Store</option>
                          <option value="restaurant_grocery_store">Restaurant & Grocery Store</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Used for AI content generation prompts in CMS</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Phone className="h-4 w-4 inline mr-2" />
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={businessInfo.phone}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Mail className="h-4 w-4 inline mr-2" />
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={businessInfo.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MapPin className="h-4 w-4 inline mr-2" />
                        Address *
                      </label>
                      <textarea
                        name="address"
                        value={businessInfo.address}
                        onChange={handleInputChange}
                        required
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Full business address"
                      />
                    </div>

                    {/* Favicon Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Globe className="h-4 w-4 inline mr-2" />
                        Favicon (Browser Tab Icon)
                      </label>
                      <input
                        type="url"
                        name="favicon_url"
                        value={businessInfo.favicon_url || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://example.com/favicon.ico"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Upload favicon to your server and paste URL here (recommended: 32x32 or 16x16 .ico or .png)
                      </p>
                      {businessInfo.favicon_url && (
                        <div className="mt-2 flex items-center">
                          <img 
                            src={businessInfo.favicon_url} 
                            alt="Favicon Preview" 
                            className="h-8 w-8 mr-2"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          <span className="text-sm text-gray-600">Preview</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Globe className="h-4 w-4 inline mr-2" />
                        Business Timezone *
                      </label>
                      <select
                        name="timezone"
                        value={businessInfo.timezone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <optgroup label="US Timezones">
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="America/Anchorage">Alaska Time (AKT)</option>
                          <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
                        </optgroup>
                        <optgroup label="Canada Timezones">
                          <option value="America/Toronto">Eastern Time - Toronto</option>
                          <option value="America/Winnipeg">Central Time - Winnipeg</option>
                          <option value="America/Edmonton">Mountain Time - Edmonton</option>
                          <option value="America/Vancouver">Pacific Time - Vancouver</option>
                        </optgroup>
                        <optgroup label="India & Asia">
                          <option value="Asia/Kolkata">India Standard Time (IST)</option>
                          <option value="Asia/Tokyo">Tokyo (JST)</option>
                          <option value="Asia/Singapore">Singapore (SGT)</option>
                          <option value="Asia/Dubai">Dubai (GST)</option>
                        </optgroup>
                        <optgroup label="Europe & Others">
                          <option value="Europe/London">London (GMT/BST)</option>
                          <option value="Europe/Paris">Paris (CET/CEST)</option>
                          <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                        </optgroup>
                      </select>
                      <p className="mt-2 text-sm text-gray-600">
                        This timezone will be used for all appointment scheduling and business hours calculations.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Building className="h-4 w-4 inline mr-2" />
                          Referral Emergency Hospital Name
                        </label>
                        <input
                          type="text"
                          name="referral_hospital_name"
                          value={businessInfo.referral_hospital_name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="VCA SouthPaws"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Emergency hospital to refer clients after hours
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Phone className="h-4 w-4 inline mr-2" />
                          Referral Hospital Phone Number
                        </label>
                        <input
                          type="tel"
                          name="referral_hospital_phone"
                          value={businessInfo.referral_hospital_phone}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="(703) 752-9100"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Emergency contact number for after-hours referrals
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Locations Tab */}
                {activeTab === 'locations' && (
                  <div className="space-y-6">
                    {/* Enable Multiple Locations Toggle */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Enable Multiple Locations</h4>
                          <p className="text-xs text-gray-600 mt-1">
                            Allow managing additional business locations beyond your primary location
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setMultipleLocationsEnabled(!multipleLocationsEnabled)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            multipleLocationsEnabled ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              multipleLocationsEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Primary Location Info (Always visible) */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <MapPin className="h-5 w-5 text-green-600 mr-2" />
                          <h4 className="text-sm font-semibold text-gray-900">Primary Location</h4>
                        </div>
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          Main
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p><strong>Name:</strong> {businessInfo.hospital_name || 'Not set'}</p>
                        <p><strong>Address:</strong> {businessInfo.address || 'Not set'}</p>
                        <p><strong>Phone:</strong> {businessInfo.phone || 'Not set'}</p>
                        <p><strong>Email:</strong> {businessInfo.email || 'Not set'}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-3 italic">
                        Edit primary location details in the &quot;Business Info&quot; tab
                      </p>
                    </div>

                    {/* Additional Locations Section (Only when enabled) */}
                    {multipleLocationsEnabled && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-gray-900">Additional Locations</h4>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingLocationIndex(null);
                              setLocationForm({ name: '', address: '', phone: '', email: '' });
                              setShowLocationModal(true);
                            }}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-md transition-colors"
                            style={{ backgroundColor: 'var(--theme-primary)' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Location
                          </button>
                        </div>

                        {/* List of Additional Locations */}
                        {additionalLocations.length === 0 ? (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                            <MapPin className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm text-gray-600">No additional locations added yet.</p>
                            <p className="text-xs text-gray-500 mt-1">Click &quot;Add Location&quot; to create your first additional location.</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {additionalLocations.map((location, index) => (
                              <div key={location.id || index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center mb-2">
                                      <MapPin className="h-4 w-4 text-blue-600 mr-2" />
                                      <h5 className="text-sm font-semibold text-gray-900">{location.name}</h5>
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1 ml-6">
                                      <p><strong>Address:</strong> {location.address || 'Not set'}</p>
                                      {location.phone && <p><strong>Phone:</strong> {location.phone}</p>}
                                      {location.email && <p><strong>Email:</strong> {location.email}</p>}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2 ml-4">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingLocationIndex(index);
                                        setLocationForm({
                                          name: location.name,
                                          address: location.address,
                                          phone: location.phone || '',
                                          email: location.email || ''
                                        });
                                        setShowLocationModal(true);
                                      }}
                                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                      title="Edit location"
                                    >
                                      <Edit3 className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedLocations = additionalLocations.filter((_, i) => i !== index);
                                        setAdditionalLocations(updatedLocations);
                                        setMessage({ type: 'success', text: 'Location removed. Click "Save Changes" to persist.' });
                                        clearMessage();
                                      }}
                                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                      title="Delete location"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <p className="text-xs text-gray-500 italic">
                          Remember to click &quot;Save Changes&quot; at the bottom to persist your location changes.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Social Media Tab */}
                {activeTab === 'social' && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Facebook className="h-4 w-4 inline mr-2 text-theme-blue" />
                          Facebook Page URL
                        </label>
                        <input
                          type="url"
                          name="facebook_link"
                          value={businessInfo.facebook_link}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://facebook.com/yourpage"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Instagram className="h-4 w-4 inline mr-2 text-pink-600" />
                          Instagram Profile URL
                        </label>
                        <input
                          type="url"
                          name="instagram_link"
                          value={businessInfo.instagram_link}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://instagram.com/yourprofile"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Twitter className="h-4 w-4 inline mr-2 text-blue-400" />
                          X Profile URL
                        </label>
                        <input
                          type="url"
                          name="twitter_link"
                          value={businessInfo.twitter_link}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://x.com/yourprofile"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Music className="h-4 w-4 inline mr-2 text-black" />
                          TikTok Profile URL
                        </label>
                        <input
                          type="url"
                          name="tiktok_link"
                          value={businessInfo.tiktok_link}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://tiktok.com/@yourprofile"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <MessageCircle className="h-4 w-4 inline mr-2 text-green-600" />
                          WhatsApp Group/Channel URL
                        </label>
                        <input
                          type="url"
                          name="whatsapp_group_link"
                          value={businessInfo.whatsapp_group_link}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://chat.whatsapp.com/..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Link to your WhatsApp group or channel for community updates
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Review Platforms Tab */}
                {activeTab === 'reviews' && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Globe className="h-4 w-4 inline mr-2 text-theme-blue" />
                          Google Reviews URL
                        </label>
                        <input
                          type="url"
                          name="google_reviews_link"
                          value={businessInfo.google_reviews_link}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://g.page/r/..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Link to your Google Business reviews page
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <ExternalLink className="h-4 w-4 inline mr-2 text-red-600" />
                          Yelp Reviews URL
                        </label>
                        <input
                          type="url"
                          name="yelp_reviews_link"
                          value={businessInfo.yelp_reviews_link}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://www.yelp.com/biz/..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Link to your Yelp business reviews page
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Facebook className="h-4 w-4 inline mr-2 text-theme-blue" />
                          Facebook Reviews URL
                        </label>
                        <input
                          type="url"
                          name="facebook_reviews_link"
                          value={businessInfo.facebook_reviews_link}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://www.facebook.com/pg/.../reviews/"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Link to your Facebook business reviews page
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* SEO Settings Tab */}
                {activeTab === 'seo' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-base font-medium text-gray-900 mb-4">Address & Location Information</h3>
                      
                      <div className="grid grid-cols-1 gap-4 mb-4">
                        {/* Street Address */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Street Address
                          </label>
                          <input
                            type="text"
                            name="street_address"
                            value={businessInfo.street_address || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                            placeholder="123 Main Street"
                          />
                        </div>

                        {/* City and Zip Code */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              City
                            </label>
                            <input
                              type="text"
                              name="city"
                              value={businessInfo.city || ''}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                              placeholder="Your City"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Zip Code
                            </label>
                            <input
                              type="text"
                              name="zip_code"
                              value={businessInfo.zip_code || ''}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                              placeholder="12345"
                            />
                          </div>
                        </div>

                        {/* Latitude and Longitude */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Latitude
                            </label>
                            <input
                              type="text"
                              name="latitude"
                              value={businessInfo.latitude || ''}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                              placeholder="38.9072"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Longitude
                            </label>
                            <input
                              type="text"
                              name="longitude"
                              value={businessInfo.longitude || ''}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                              placeholder="-77.0369"
                            />
                          </div>
                        </div>

                        {/* Manual Refresh Button */}
                        <div className="flex items-center space-x-2 bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <button
                            type="button"
                            onClick={handleGeocodeAddress}
                            disabled={geocoding}
                            className="px-4 py-2 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                          >
                            {geocoding ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Getting Coordinates...
                              </>
                            ) : (
                              <>
                                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Get Coordinates
                              </>
                            )}
                          </button>
                          <div>
                            <p className="text-sm font-medium text-blue-900">
                              Click to auto-populate from Google Place ID
                            </p>
                            <p className="text-xs text-blue-700">
                              Uses the Google Place ID configured in CMS Settings → Google Integrations
                            </p>
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-gray-500 mt-4">
                        These fields are used for SEO optimization and will appear in search results. Click &quot;Manual Refresh&quot; to automatically fill address details and calculate coordinates from the main address field.
                      </p>
                    </div>
                  </div>
                )}

                {/* Placeholders Tab */}
                {activeTab === 'placeholders' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Business Placeholders</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPlaceholder(null);
                          setPlaceholderForm({ name: '', placeholder: '', value: '', description: '' });
                          setShowPlaceholderModal(true);
                        }}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md transition-colors"
                        style={{ backgroundColor: 'var(--theme-primary)' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Placeholder
                      </button>
                    </div>

                    {/* Placeholders List */}
                    <div className="bg-white rounded-lg border border-gray-200">
                      {loadingPlaceholders ? (
                        <div className="p-8 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto mb-4"></div>
                          <p className="text-gray-600">Loading placeholders...</p>
                        </div>
                      ) : businessPlaceholders.length === 0 ? (
                        <div className="p-8 text-center">
                          <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h4 className="text-lg font-medium text-gray-900 mb-2">No Business Placeholders</h4>
                          <p className="text-gray-600 mb-4">Create your first business placeholder to get started.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {businessPlaceholders.map((placeholder) => (
                            <div key={placeholder.id} className="p-4 hover:bg-gray-50">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 space-y-2">
                                  {/* Placeholder Name */}
                                  <div className="text-sm font-medium text-gray-900">
                                    {placeholder.name}
                                  </div>
                                  
                                  {/* Placeholder Value */}
                                  <div className="text-sm text-gray-700 font-mono bg-gray-50 px-2 py-1 rounded">
                                    {placeholder.value}
                                  </div>
                                  
                                  {/* Actual Placeholder Code */}
                                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded border" style={{ color: 'var(--theme-primary)' }}>
                                    {placeholder.placeholder}
                                  </code>
                                  
                                  {/* Placeholder Description */}
                                  {placeholder.description && (
                                    <p className="text-xs text-gray-500">{placeholder.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2 ml-4">
                                  <button
                                    type="button"
                                    onClick={() => handleEditBusinessPlaceholder(placeholder)}
                                    className="p-2 text-gray-400 hover:text-theme-blue transition-colors"
                                    title="Edit placeholder"
                                  >
                                    <Edit3 className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => confirmDeleteBusinessPlaceholder(placeholder.id, placeholder.name)}
                                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                    title="Delete placeholder"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 pt-6 border-t border-gray-200 mt-8">
                  {/* Sync to Google Button (only show on Business tab) */}
                  {activeTab === 'business' && (
                    <button
                      type="button"
                      onClick={handleGoogleSync}
                      disabled={syncing}
                      className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                      style={{ 
                        backgroundColor: syncing ? '#94a3b8' : '#10b981'
                      }}
                      onMouseEnter={(e) => {
                        if (!syncing) e.target.style.backgroundColor = '#059669';
                      }}
                      onMouseLeave={(e) => {
                        if (!syncing) e.target.style.backgroundColor = '#10b981';
                      }}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                      {syncing ? 'Syncing...' : 'Sync to Google'}
                    </button>
                  )}
                  
                  {/* Save Changes Button */}
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                    style={{ backgroundColor: saving ? '#94a3b8' : 'var(--theme-primary)' }}
                    onMouseEnter={(e) => {
                      if (!saving) e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                    }}
                    onMouseLeave={(e) => {
                      if (!saving) e.target.style.backgroundColor = 'var(--theme-primary)';
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Business Placeholder Modal */}
        {showPlaceholderModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <h3 className="text-lg font-medium text-white">
                  {editingPlaceholder ? 'Edit Business Placeholder' : 'Add Business Placeholder'}
                </h3>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={placeholderForm.name}
                    onChange={(e) => setPlaceholderForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Promotional Link"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Placeholder Code *
                  </label>
                  <input
                    type="text"
                    value={placeholderForm.placeholder}
                    onChange={(e) => setPlaceholderForm(prev => ({ ...prev, placeholder: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., PROMO_LINK or [PROMO_LINK]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Will be automatically formatted as [PLACEHOLDER_NAME] if brackets are missing
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Value *
                  </label>
                  <input
                    type="text"
                    value={placeholderForm.value}
                    onChange={(e) => setPlaceholderForm(prev => ({ ...prev, value: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., https://yoursite.com/promo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={placeholderForm.description}
                    onChange={(e) => setPlaceholderForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="Optional description of what this placeholder represents"
                  />
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelPlaceholderForm}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={editingPlaceholder ? handleUpdateBusinessPlaceholder : handleCreateBusinessPlaceholder}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                >
                  {editingPlaceholder ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Business Placeholder Confirmation Modal */}
        {showBusinessConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <h3 className="text-lg font-medium text-white">Confirm Deletion</h3>
              </div>
              
              {/* Content */}
              <div className="px-6 py-4">
                <p className="text-sm text-gray-700">{businessConfirmMessage}</p>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowBusinessConfirmModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    businessConfirmAction();
                    setShowBusinessConfirmModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors"
                  style={{ backgroundColor: '#dc2626' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Location Modal */}
        {showLocationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <h3 className="text-lg font-medium text-white">
                  {editingLocationIndex !== null ? 'Edit Location' : 'Add New Location'}
                </h3>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location Name *
                  </label>
                  <input
                    type="text"
                    value={locationForm.name}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Downtown Branch"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <textarea
                    value={locationForm.address}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="Full address of this location"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={locationForm.phone}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={locationForm.email}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="branch@example.com"
                  />
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowLocationModal(false);
                    setEditingLocationIndex(null);
                    setLocationForm({ name: '', address: '', phone: '', email: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!locationForm.name.trim() || !locationForm.address.trim()) {
                      setMessage({ type: 'error', text: 'Location name and address are required' });
                      clearMessage();
                      return;
                    }

                    if (editingLocationIndex !== null) {
                      // Update existing location
                      const updatedLocations = [...additionalLocations];
                      updatedLocations[editingLocationIndex] = {
                        ...updatedLocations[editingLocationIndex],
                        name: locationForm.name,
                        address: locationForm.address,
                        phone: locationForm.phone,
                        email: locationForm.email
                      };
                      setAdditionalLocations(updatedLocations);
                      setMessage({ type: 'success', text: 'Location updated. Click "Save Changes" to persist.' });
                    } else {
                      // Add new location
                      const newLocation = {
                        id: `loc_${Date.now()}`,
                        name: locationForm.name,
                        address: locationForm.address,
                        phone: locationForm.phone,
                        email: locationForm.email
                      };
                      setAdditionalLocations([...additionalLocations, newLocation]);
                      setMessage({ type: 'success', text: 'Location added. Click "Save Changes" to persist.' });
                    }

                    clearMessage();
                    setShowLocationModal(false);
                    setEditingLocationIndex(null);
                    setLocationForm({ name: '', address: '', phone: '', email: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                >
                  {editingLocationIndex !== null ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessInfo;